import React, { useState, useEffect, useCallback } from 'react';

const fmtDt = (v) => {
  if (!v) return '-';
  const d = new Date(v);
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};
const fmtCur = (v) => {
  const n = Number(v || 0);
  return n.toLocaleString('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const riskColors = { HIGH: 'var(--theme-danger)', MEDIUM: '#ca8a04', LOW: 'var(--theme-success)' };
const riskBg = { HIGH: '#fef2f2', MEDIUM: '#fffbeb', LOW: '#f0fdf4' };
const statusColors = { ACTIVE: '#2563eb', ACTIONED: 'var(--theme-success)', EXPIRED: 'var(--theme-text-muted)' };

function PmsNoShowPredictor({ token, selectedProperty }) {
  const [predictions, setPredictions] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [scoring, setScoring] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [lastGenResult, setLastGenResult] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [actioningId, setActioningId] = useState(null);
  const [actionNote, setActionNote] = useState('');
  const [showActionModal, setShowActionModal] = useState(null);
  const [riskFilter, setRiskFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const headers = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
  }), [token, selectedProperty]);

  const loadPredictions = useCallback(async () => {
    if (!selectedProperty?.id) return;
    setLoading(true); setError('');
    try {
      let url = `/api/v1/ai/noshow-predictor/predictions?`;
      if (riskFilter) url += `&risk_level=${riskFilter}`;
      if (statusFilter) url += `&status=${statusFilter}`;
      const res = await fetch(url, { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load');
      setPredictions(data.predictions || []);
      setSummary(data.summary || {});
    } catch (err) {
      setError(err.message);
    } finally { setLoading(false); }
  }, [selectedProperty, headers, riskFilter, statusFilter]);

  useEffect(() => { loadPredictions(); }, [loadPredictions]);

  const scoreReservations = async () => {
    setScoring(true); setError(''); setMessage(''); setLastGenResult(null);
    try {
      const res = await fetch('/api/v1/ai/noshow-predictor/generate', { method: 'POST', headers: headers(), body: '{}' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Scoring failed');
      setLastGenResult(data);
      if (data.scored_count === 0) {
        setMessage(data.message || 'No upcoming reservations to score');
      } else {
        const tokens = (Number(data.usage?.input_tokens) || 0) + (Number(data.usage?.output_tokens) || 0);
        setMessage(`Scored ${data.scored_count} reservations — ${data.summary?.high_risk || 0} HIGH, ${data.summary?.medium_risk || 0} MEDIUM risk (${data.response_time_ms}ms, ${tokens.toLocaleString('en-IN')} tokens)`);
      }
      await loadPredictions();
    } catch (err) {
      setError(err.message);
    } finally { setScoring(false); }
  };

  const markActioned = async (id) => {
    setActioningId(id);
    try {
      const res = await fetch(`/api/v1/ai/noshow-predictor/predictions/${id}/action`, {
        method: 'POST', headers: headers(), body: JSON.stringify({ note: actionNote }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Action failed');
      setMessage('Prediction marked as actioned');
      setShowActionModal(null);
      setActionNote('');
      await loadPredictions();
    } catch (err) {
      setError(err.message);
    } finally { setActioningId(null); }
  };

  /* ─── styles ───────────────────────────────────── */
  const card = { background: '#fff', borderRadius: 8, padding: '16px 20px', boxShadow: '0 1px 3px rgba(0,0,0,.08)', flex: 1, minWidth: 150 };
  const kpiLabel = { fontSize: 12, color: 'var(--theme-text-secondary)', marginBottom: 4 };
  const kpiValue = { fontSize: 24, fontWeight: 700 };
  const badge = (color, bg) => ({
    display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
    color, background: bg || (color + '18'),
  });
  const btn = (bg, color = '#fff') => ({
    padding: '8px 18px', borderRadius: 6, border: 'none', background: bg, color,
    fontWeight: 600, cursor: 'pointer', fontSize: 14,
  });

  return (
    <div style={{ padding: 24, maxWidth: 1300, margin: '0 auto' }}>
      <h2 style={{ marginBottom: 4, fontSize: 22 }}>No-Show & Cancellation Predictor</h2>
      <p style={{ color: 'var(--theme-text-secondary)', marginBottom: 20, fontSize: 14 }}>AI-powered risk scoring for upcoming reservations</p>

      {error && <div style={{ background: '#fef2f2', color: 'var(--theme-danger)', padding: '10px 16px', borderRadius: 6, marginBottom: 16 }}>{error}</div>}
      {message && <div style={{ background: '#f0fdf4', color: 'var(--theme-success)', padding: '10px 16px', borderRadius: 6, marginBottom: 16 }}>{message}</div>}

      {/* KPI Cards */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={card}>
          <div style={kpiLabel}>Total Scored</div>
          <div style={{ ...kpiValue, color: '#2563eb' }}>{summary.total || 0}</div>
        </div>
        <div style={card}>
          <div style={kpiLabel}>High Risk</div>
          <div style={{ ...kpiValue, color: 'var(--theme-danger)' }}>{summary.high_risk_count || 0}</div>
        </div>
        <div style={card}>
          <div style={kpiLabel}>Medium Risk</div>
          <div style={{ ...kpiValue, color: '#ca8a04' }}>{summary.medium_risk_count || 0}</div>
        </div>
        <div style={card}>
          <div style={kpiLabel}>At-Risk Revenue</div>
          <div style={{ ...kpiValue, color: '#7c3aed', fontSize: 20 }}>{fmtCur(summary.at_risk_revenue || 0)}</div>
        </div>
      </div>

      {/* Action Bar */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <button onClick={scoreReservations} disabled={scoring} style={btn(scoring ? 'var(--theme-text-muted)' : '#2563eb')}>
          {scoring ? 'Scoring...' : 'Score Reservations'}
        </button>
        <select value={riskFilter} onChange={e => setRiskFilter(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14 }}>
          <option value="">All Risk Levels</option>
          <option value="HIGH">HIGH</option>
          <option value="MEDIUM">MEDIUM</option>
          <option value="LOW">LOW</option>
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14 }}>
          <option value="">All Status</option>
          <option value="ACTIVE">Active</option>
          <option value="ACTIONED">Actioned</option>
        </select>
      </div>

      {/* AI Result Summary */}
      {lastGenResult && lastGenResult.scored_count > 0 && (
        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: 16, marginBottom: 20 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>AI Scoring Summary</div>
          <div style={{ fontSize: 13, color: '#1e40af' }}>
            {lastGenResult.summary?.high_risk || 0} High Risk &bull; {lastGenResult.summary?.medium_risk || 0} Medium Risk &bull; {lastGenResult.summary?.low_risk || 0} Low Risk
            &bull; At-Risk Revenue: {fmtCur(lastGenResult.summary?.at_risk_revenue || 0)}
          </div>
          <div style={{ fontSize: 12, color: 'var(--theme-text-secondary)', marginTop: 4 }}>
            Tokens: {lastGenResult.usage?.input_tokens || 0} in / {lastGenResult.usage?.output_tokens || 0} out
            &bull; Time: {lastGenResult.response_time_ms || 0}ms
          </div>
        </div>
      )}

      {/* Predictions Table */}
      <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,.08)', overflow: 'auto' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--theme-text-muted)' }}>Loading predictions...</div>
        ) : predictions.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--theme-text-muted)' }}>
            No predictions yet. Click "Score Reservations" to analyze upcoming arrivals.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--theme-bg-light)', borderBottom: '2px solid var(--theme-border)' }}>
                {['Guest', 'Room', 'Arrival', 'Departure', 'Source', 'Advance / Total', 'Risk Score', 'Risk Level', 'Recommended Action', 'Status', ''].map(h => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: '#475569', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {predictions.map(p => {
                const isExpanded = expandedId === p.id;
                const factors = Array.isArray(p.risk_factors) ? p.risk_factors : (typeof p.risk_factors === 'string' ? JSON.parse(p.risk_factors || '[]') : []);
                const guestHist = typeof p.guest_history === 'string' ? JSON.parse(p.guest_history || '{}') : (p.guest_history || {});
                const advPct = p.total_amount > 0 ? Math.round((Number(p.advance_amount) / Number(p.total_amount)) * 100) : 0;
                const rowBg = p.risk_level === 'HIGH' ? '#fef2f2' : p.risk_level === 'MEDIUM' ? '#fffbeb' : 'transparent';

                return (
                  <React.Fragment key={p.id}>
                    <tr
                      style={{ borderBottom: '1px solid var(--theme-border-light)', cursor: 'pointer', background: rowBg }}
                      onClick={() => setExpandedId(isExpanded ? null : p.id)}
                    >
                      <td style={{ padding: '10px 12px', fontWeight: 500 }}>{p.guest_name || '-'}</td>
                      <td style={{ padding: '10px 12px' }}>{p.room_number || 'TBA'}</td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>{fmtDt(p.arrival_date)}</td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>{fmtDt(p.departure_date)}</td>
                      <td style={{ padding: '10px 12px' }}>{p.booking_source || '-'}</td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                        {fmtCur(p.advance_amount)} / {fmtCur(p.total_amount)}
                        <span style={{ fontSize: 11, color: advPct < 30 ? 'var(--theme-danger)' : 'var(--theme-success)', marginLeft: 4 }}>({advPct}%)</span>
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 60, height: 8, borderRadius: 4, background: 'var(--theme-border)', overflow: 'hidden' }}>
                            <div style={{
                              width: `${p.noshow_score}%`, height: '100%', borderRadius: 4,
                              background: riskColors[p.risk_level] || 'var(--theme-text-muted)',
                            }} />
                          </div>
                          <span style={{ fontWeight: 600, color: riskColors[p.risk_level], fontSize: 13 }}>{p.noshow_score}</span>
                        </div>
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={badge(riskColors[p.risk_level], riskBg[p.risk_level])}>
                          {p.risk_level}
                        </span>
                      </td>
                      <td style={{ padding: '10px 12px', maxWidth: 200, fontSize: 12, color: '#475569' }}>
                        {p.recommended_action || '-'}
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={badge(statusColors[p.status] || 'var(--theme-text-muted)')}>
                          {p.status}
                        </span>
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        {p.status === 'ACTIVE' && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setShowActionModal(p.id); setActionNote(''); }}
                            style={{ ...btn('#059669'), padding: '4px 12px', fontSize: 12 }}
                          >
                            Action
                          </button>
                        )}
                      </td>
                    </tr>
                    {/* Expanded Risk Factors */}
                    {isExpanded && (
                      <tr style={{ background: 'var(--theme-bg-light)' }}>
                        <td colSpan={11} style={{ padding: '12px 24px' }}>
                          <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
                            {/* Risk Factors */}
                            <div style={{ flex: 2, minWidth: 300 }}>
                              <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>Risk Factors</div>
                              {factors.length === 0 ? (
                                <div style={{ color: 'var(--theme-text-muted)', fontSize: 13 }}>No factor breakdown available</div>
                              ) : (
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                  <thead>
                                    <tr style={{ borderBottom: '1px solid var(--theme-border)' }}>
                                      <th style={{ padding: '6px 8px', textAlign: 'left', color: 'var(--theme-text-secondary)', fontWeight: 600 }}>Factor</th>
                                      <th style={{ padding: '6px 8px', textAlign: 'left', color: 'var(--theme-text-secondary)', fontWeight: 600 }}>Detail</th>
                                      <th style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--theme-text-secondary)', fontWeight: 600 }}>Impact</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {factors.map((f, i) => (
                                      <tr key={i} style={{ borderBottom: '1px solid var(--theme-border-light)' }}>
                                        <td style={{ padding: '6px 8px', fontWeight: 500 }}>{f.factor}</td>
                                        <td style={{ padding: '6px 8px', color: '#475569' }}>{f.detail}</td>
                                        <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600, color: f.impact > 0 ? 'var(--theme-danger)' : 'var(--theme-success)' }}>
                                          {f.impact > 0 ? '+' : ''}{f.impact}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                            {/* Guest History */}
                            <div style={{ flex: 1, minWidth: 180 }}>
                              <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>Guest History</div>
                              <div style={{ fontSize: 12, lineHeight: 1.8, color: '#475569' }}>
                                <div>Total Past Stays: <strong>{guestHist.total_stays || 0}</strong></div>
                                <div>Past No-Shows: <strong style={{ color: guestHist.past_noshows > 0 ? 'var(--theme-danger)' : 'inherit' }}>{guestHist.past_noshows || 0}</strong></div>
                                <div>Past Cancellations: <strong>{guestHist.past_cancellations || 0}</strong></div>
                                <div>New Guest: <strong>{guestHist.is_new_guest ? 'Yes' : 'No'}</strong></div>
                              </div>
                            </div>
                          </div>
                          {/* Actioned Note */}
                          {p.status === 'ACTIONED' && p.actioned_note && (
                            <div style={{ marginTop: 12, padding: '8px 12px', background: '#f0fdf4', borderRadius: 6, fontSize: 12 }}>
                              <strong>Action Note:</strong> {p.actioned_note}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Action Modal */}
      {showActionModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 420, maxWidth: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,.2)' }}>
            <h3 style={{ marginTop: 0, marginBottom: 12 }}>Mark as Actioned</h3>
            <p style={{ fontSize: 13, color: 'var(--theme-text-secondary)', marginBottom: 12 }}>Record what action was taken for this at-risk reservation.</p>
            <textarea
              value={actionNote}
              onChange={e => setActionNote(e.target.value)}
              placeholder="e.g., Called guest and confirmed arrival, Sent confirmation email..."
              style={{ width: '100%', minHeight: 80, padding: 10, borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
              <button onClick={() => { setShowActionModal(null); setActionNote(''); }} style={btn('var(--theme-border-light)', '#475569')}>Cancel</button>
              <button
                onClick={() => markActioned(showActionModal)}
                disabled={actioningId === showActionModal}
                style={btn('#059669')}
              >
                {actioningId === showActionModal ? 'Saving...' : 'Confirm Action'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PmsNoShowPredictor;
